import React, { useMemo } from "react";
import { UserCheck, ExternalLink, Video, MapPin } from "lucide-react";

/**
 * Painel de "Profissionais indicados" — extrai attachments `professional_card`
 * das mensagens do chat e renderiza como cards prontos para a família decidir.
 * Fica logo abaixo da Central de Documentos.
 */
export const IndicatedProfessionalsPanel = ({ messages, onAccept, onDecline }) => {
  const cards = useMemo(() => {
    const byId = new Map();
    (messages || []).forEach((m) => {
      const a = m.attachment;
      if (a?.type === "professional_card" && a.professional_id) {
        byId.set(a.professional_id, {
          ...a.snapshot,
          professional_id: a.professional_id,
          indicated_at: m.created_at,
          message_id: m.id,
        });
      }
    });
    return Array.from(byId.values());
  }, [messages]);

  if (cards.length === 0) return null;

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-6" data-testid="indicated-professionals-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-display font-bold text-xl text-[#0B2B62] flex items-center gap-2">
          <UserCheck className="w-5 h-5 text-violet-600" />
          Profissionais indicados pelo consultor
        </h2>
        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
          {cards.length} sugest{cards.length > 1 ? "ões" : "ão"}
        </span>
      </div>

      <p className="text-xs text-slate-500 mb-4 leading-relaxed">
        O consultor selecionou {cards.length > 1 ? "estes profissionais" : "este profissional"} para
        você com base na sua triagem. Veja o perfil completo e diga ao consultor no chat qual você
        prefere para agendar a primeira consulta online.
      </p>

      <div className="grid sm:grid-cols-2 gap-3">
        {cards.map((c) => (
          <div key={c.professional_id} data-testid={`indicated-prof-${c.professional_id}`}
            className="border-2 border-violet-100 hover:border-violet-400 transition-colors rounded-lg overflow-hidden">
            <div className="p-3 flex items-start gap-3">
              {c.photo_url ? (
                <img src={c.photo_url} alt={c.name} loading="lazy"
                  className="w-14 h-14 rounded-full object-cover border border-slate-200 shrink-0" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-500 font-bold text-sm shrink-0">
                  {(c.name || "P").split(" ").map((x) => x[0]).slice(0, 2).join("")}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="font-display font-bold text-[#0B2B62] truncate">{c.name}</div>
                {c.council && <div className="text-[10px] text-slate-500 uppercase tracking-widest">{c.council}</div>}
                {(c.categories || []).length > 0 && (
                  <div className="text-xs text-slate-700 mt-1">{(c.categories || []).slice(0, 3).join(" · ")}</div>
                )}
                <div className="mt-1 text-xs text-slate-500 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span className="inline-flex items-center gap-0.5 text-emerald-700 font-semibold"><Video className="w-3 h-3" /> Online</span>
                  {c.city && <span className="inline-flex items-center gap-0.5"><MapPin className="w-3 h-3" />{c.city}</span>}
                  {c.session_value != null && (
                    <span className="text-slate-800 font-semibold">
                      R$ {Number(c.session_value).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}/sessão
                    </span>
                  )}
                </div>
                {c.bio && <div className="text-[11px] text-slate-600 mt-1.5 line-clamp-2">{c.bio}</div>}
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 border-t border-slate-100 px-3 py-2 bg-slate-50">
              <a href={c.profile_url} target="_blank" rel="noreferrer"
                data-testid={`indicated-view-${c.professional_id}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#0B2B62] hover:bg-slate-800 text-white text-xs font-bold">
                <ExternalLink className="w-3 h-3" /> Ver perfil completo
              </a>
              {onAccept && (
                <button onClick={() => onAccept(c)}
                  data-testid={`indicated-accept-${c.professional_id}`}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-emerald-600 text-emerald-700 hover:bg-emerald-50 text-xs font-bold">
                  Aceitar sugestão
                </button>
              )}
              {onDecline && (
                <button onClick={() => onDecline(c)}
                  data-testid={`indicated-decline-${c.professional_id}`}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-100 text-xs font-semibold">
                  Pedir outra opção
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
